import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import type {
  RevealedIndicativePrices,
  RevealedTowerViewUnitPrices,
} from './superPickerPublicApi';

/**
 * App-wide cache for prices the recipient has already spent a token to
 * reveal. Sections are keyed by `(section, sectionKey)` so re-renders and
 * cross-component reads (e.g. PDF export) can pick up unmasked data without
 * burning another token.
 *
 * Each section's value type matches the corresponding branch of
 * `RevealResponse` minus the token-counter fields (those live in
 * `shareContext`).
 *
 * Extend `RevealedPricesStore` as new sections come online.
 */
export interface RevealedPricesStore {
  /**
   * `projectId` → unmasked indicative price ranges. Empty when no project
   * has been revealed yet.
   */
  projectDetails: Record<string, RevealedIndicativePrices | null>;
  /**
   * `projectId` → map of synthetic unit key (`block-unitNumber`) to its
   * unmasked numeric prices. Populated by the `towerView` reveal flow and
   * read by `AvailableUnitsSection` (and any other component that renders
   * the same masked tower-view payload, e.g. the Best PX / Best PSF tabs
   * which are derived from the same units).
   */
  towerView: Record<string, Record<string, RevealedTowerViewUnitPrices>>;
}

const EMPTY_STORE: RevealedPricesStore = {
  projectDetails: {},
  towerView: {},
};

interface RevealedPricesContextValue {
  store: RevealedPricesStore;
  /**
   * Stash a reveal-response payload for later lookups. Idempotent: calling
   * with the same key just overwrites the previous value.
   */
  setRevealed: <K extends keyof RevealedPricesStore>(
    section: K,
    key: string,
    value: RevealedPricesStore[K][string],
  ) => void;
  /**
   * Bulk-seed the cache from a hydration payload (e.g. the response of
   * `GET /share/:uuid/reveals` on SPA boot). Replaces the entire store —
   * use sparingly. For incremental writes prefer `setRevealed`.
   */
  seedRevealed: (next: RevealedPricesStore) => void;
  /** Clear every cached reveal — used on share-link sign-out flows. */
  resetRevealed: () => void;
}

const RevealedPricesContext = createContext<RevealedPricesContextValue | null>(null);

/**
 * Provider mounted near the root of the public SPA. Owns the in-memory
 * unmask cache; lives only for the recipient's current browser session
 * (refresh → cache cleared → tokens already spent stay spent on the
 * server, so the recipient would need to pay again to re-reveal).
 */
export const RevealedPricesProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [store, setStore] = useState<RevealedPricesStore>(EMPTY_STORE);

  const setRevealed = useCallback(
    <K extends keyof RevealedPricesStore>(
      section: K,
      key: string,
      value: RevealedPricesStore[K][string],
    ): void => {
      setStore((prev) => ({
        ...prev,
        [section]: { ...prev[section], [key]: value } as RevealedPricesStore[K],
      }));
    },
    [],
  );

  const seedRevealed = useCallback((next: RevealedPricesStore) => {
    setStore(next);
  }, []);

  const resetRevealed = useCallback(() => {
    setStore(EMPTY_STORE);
  }, []);

  const value = useMemo<RevealedPricesContextValue>(
    () => ({ store, setRevealed, seedRevealed, resetRevealed }),
    [store, setRevealed, seedRevealed, resetRevealed],
  );

  return (
    <RevealedPricesContext.Provider value={value}>
      {children}
    </RevealedPricesContext.Provider>
  );
};

/**
 * Read the unmasked value for a single (section, key) pair. Returns
 * `undefined` when the recipient has not yet revealed it (caller falls
 * back to the masked string).
 *
 * Components that need to *write* should call `useRevealedPrices()` and
 * use `setRevealed` directly — typically from the dialog's `onConfirmed`.
 */
export function useRevealedSection<K extends keyof RevealedPricesStore>(
  section: K,
  key: string | null | undefined,
): RevealedPricesStore[K][string] | undefined {
  const ctx = useContext(RevealedPricesContext);
  if (!ctx) return undefined;
  if (!key) return undefined;
  // Generic indexed-access narrowing trips TS; the runtime semantics are
  // sound (record lookup), so we cast back to the parametrised value type.
  return ctx.store[section][key] as RevealedPricesStore[K][string] | undefined;
}

/**
 * Full context accessor for components that need both reads and writes
 * (e.g. the price chip that pays for + receives the unmask).
 */
export function useRevealedPrices(): RevealedPricesContextValue {
  const ctx = useContext(RevealedPricesContext);
  if (!ctx) {
    throw new Error(
      'useRevealedPrices must be used within a <RevealedPricesProvider>',
    );
  }
  return ctx;
}
